/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import {
    HTTPHdrActionType,
    IHTTPRequestRule,
} from 'generated-types';

import { HTTPRequestRule } from 'object-types';
import { L10nService } from '@vmw/ngx-vip';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { RepeatedMessageItem } from 'ajs/modules/data-model/factories/repeated-message-item.factory';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { MatchTargetConfigItem } from 'ajs/modules/policies/factories/config-items/match-target.config-item.factory';
import { HTTPRedirectActionConfigItem } from 'ng/modules/message-items/factories';
import { HTTPRewriteURLActionConfigItem }
    from 'ajs/modules/http-policy-set/factories/http-request/http-rewrite-url-action.config-item.factory';

import { HTTPSwitchingActionConfigItem }
    from 'ajs/modules/http-policy-set/factories/http-request/http-switching-action.config-item.factory';

import { HTTPHdrActionConfigItem } from 'ajs/modules/http-policy-set/factories/http-hdr-action.config-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

import { DefaultValues } from 'ajs/modules/core/services/default-values.service';
import * as l10n from '../../http-policy-set.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type THTTRequestRulePartial = Omit<IHTTPRequestRule, 'match'
| 'redirect_action' | 'rewrite_url_action' | 'switching_action' | 'hdr_action'>;

interface IHTTPRequestRuleConfig extends THTTRequestRulePartial {
    match?: MatchTargetConfigItem;
    redirect_action?: HTTPRedirectActionConfigItem;
    rewrite_url_action?: HTTPRewriteURLActionConfigItem;
    switching_action?: HTTPSwitchingActionConfigItem;
    hdr_action?: RepeatedMessageItem<HTTPHdrActionConfigItem>;
}

/**
 * Enums for fields in HTTPRequestRule Message.
 * Used to show dropdown option on the Template.
 */
export const enum HTTPRequestRuleActionFields {
    HDR_ACTION = 'hdr_action',
    REWRITE_URL_ACTION = 'rewrite_url_action',
    REDIRECT_ACTION = 'redirect_action',
    SWITCHING_ACTION = 'switching_action',
}

/**
 * Default Header Action config value.
 * Used to instantiate hdr_action Message.
 */
const defaultHdrActionValue = {
    action: HTTPHdrActionType.HTTP_REMOVE_HDR,
    hdr: {
        name: '',
        value: {},
    },
};

/**
 * @description HTTPPolicySet Request rules Message Item.
 * @author Abhinesh Gour, Umesh Chovatiya.
 */
export class HTTPRequestRuleConfigItem extends
    withFullModalMixin(MessageItem)<IHTTPRequestRuleConfig> {
    public static ajsDependencies = [
        'l10nService',
    ];

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPRequestRule,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Import Lazy Loaded Modal Component.
     */
    /* eslint-disable-next-line */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { HTTPRequestRuleModalComponent } = await import(
            /* webpackChunkName: "http-policy-set" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/http-policy-set-modal/components/http-request/http-request-rule-modal/http-request-rule-modal.component'
        );

        return HTTPRequestRuleModalComponent as Type<Component>;
    }

    /**
     * Getter for Redirect action config.
     */
    public get redirectAction() : HTTPRedirectActionConfigItem {
        const { redirect_action: redirectActionConfig } = this.config;

        return redirectActionConfig;
    }

    /**
     * Getter for Rewrite URL action config.
     */
    public get rewriteUrlAction() : HTTPRewriteURLActionConfigItem {
        const { rewrite_url_action: rewriteUrlActionConfig } = this.config;

        return rewriteUrlActionConfig;
    }

    /**
     * Getter for Header Action config.
     */
    public get headerAction() : RepeatedMessageItem<HTTPHdrActionConfigItem> {
        const { hdr_action: headerActionConfig } = this.config;

        return headerActionConfig;
    }

    /**
     * Returns true if rule has any action configured.
     */
    public hasAnyAction(): boolean {
        return Boolean(this.config.hdr_action?.count || this.config.rewrite_url_action ||
            this.config.switching_action || this.config.redirect_action);
    }

    /**
     * Clears Action based on fieldname.
     */
    public clearActionByFieldName(fieldName: string): void {
        if (fieldName) {
            delete this.config[fieldName];
        }
    }

    /**
     * Triggered for add Header Action.
     * Adds 'hdr_action' repeated message with default values.
     */
    public addHeaderAction(): void {
        this.config.hdr_action.add(defaultHdrActionValue);
    }

    /**
     * Triggered for remove Header Action.
     * Removes 'hdr_action' repeated message for given index.
     */
    public removeHeaderAction(index: number): void {
        this.config.hdr_action.remove(index);
    }

    /**
     * Add Action Configs based on fieldname.
     */
    public addActionConfigsByFieldName(fieldName: string): void {
        this.config[fieldName] = this.createChildByField(fieldName);

        /**
         * Redirect Action is mutually exclusive, selecting this action
         * will clear all other actions selected by user or are already
         * available in the config.
         */
        if (fieldName === HTTPRequestRuleActionFields.REDIRECT_ACTION) {
            this.clearAllNonRedirectActions();
        }

        // Add repeated msg item for hdr_action with default values.
        if (fieldName === HTTPRequestRuleActionFields.HDR_ACTION) {
            this.addHeaderAction();
        }
    }

    public clearAllNonRedirectActions(): void {
        this.clearActionByFieldName(HTTPRequestRuleActionFields.HDR_ACTION);
        this.clearActionByFieldName(HTTPRequestRuleActionFields.REWRITE_URL_ACTION);
        this.clearActionByFieldName(HTTPRequestRuleActionFields.SWITCHING_ACTION);
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPRequestRuleConfig> {
        const defaultValues: DefaultValues = this.getAjsDependency_('defaultValues');
        const type = this.objectType.toLowerCase();
        const defaultConfig = defaultValues.getDefaultItemConfigByType(type) || {};

        return {
            ...defaultConfig,
            rewrite_url_action: undefined,
            redirect_action: undefined,
            match: null,
        };
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.httpRequestRuleModalBreadcrumbTitle);
    }
}
